import * as React from 'react';

import {useQuery, useApolloClient} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetNewsletter as GetNewsletterQuery,
    GetNewsletterVariables as GetNewsletterQueryVariables,
    NewsletterFragment as Newsletter,
    NewsletterStubFragment as NewsletterStub,
} from 'nutshell-graphql-types';

import NEWSLETTER_STUB_FRAGMENT from './graphql/fragments/newsletter-stub-fragment.graphql';
import GET_NEWSLETTER from './graphql/queries/get-newsletter.graphql';

type Props = {
    newsletterId: string;
    children: (arg1: {
        newsletter: Newsletter | null | undefined;
        newsletterStub: NewsletterStub | null | undefined;
        isLoading: boolean;
        error: string | null | undefined;
        refetch: () => Promise<any>;
    }) => React.ReactElement;
};

export function NewsletterQuery(props: Props) {
    const client = useApolloClient();
    let newsletterStub: NewsletterStub | null | undefined;

    try {
        newsletterStub = client.readFragment({
            id: props.newsletterId,
            fragmentName: 'NewsletterStubFragment',
            fragment: NEWSLETTER_STUB_FRAGMENT,
        });
    } catch {
        // Empty
    }

    const {
        data,
        error,
        loading: isLoading,
        refetch,
    } = useQuery<GetNewsletterQuery, GetNewsletterQueryVariables>(GET_NEWSLETTER, {
        variables: {newsletterId: props.newsletterId},
        notifyOnNetworkStatusChange: true,
    });

    // This could be used to fetch a broadcast, which is why this throws a flow
    // error
    const newsletter = data ? data.campaign : null;

    return props.children({
        newsletter,
        newsletterStub,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        refetch,
    });
}
